/**
 * Editor Store
 *
 * This is the main Pinia store for the ad editor. It holds every ad
 * (one canvas at one size), every layer (text, image, shape, video,
 * audio, or element block), plus selection, viewport, and feature
 * flag state.
 *
 * Canvas services, property panels, the timeline, and history all
 * read and write through this store, so it is the single source of
 * truth for what the editor currently looks like.
 *
 * The one thing to remember: a layer's props live in one shared
 * place, but each ad can override any prop just for itself via
 * `layer.overrides[adId]`. Getters like `getResolvedProps` merge the
 * shared props, the ad's overrides, and any attribute-mapped values
 * into the final props used to render that layer for that ad.
 */

import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import type { Canvas, FabricObject } from 'fabric';
import { LAYER_PROPS, LAYER_TYPES } from '@/constants/layers';
import { ADVANCED_FEATURES } from '@/constants/features';
import {
  ATTRIBUTE_VALUE_TYPES,
  FONT_FAMILY_KEY,
  FONT_SCALE_KEY,
  LANGUAGE_KEY,
  RADIUS_SCALE_KEY,
  SHAPE_KEY
} from '@/constants/attributeValueTypes';
import { TEXT_MODE, TEXT_PROPS } from '@/constants/text';
import {
  DEFAULT_SHAPE_COLOR,
  DEFAULT_SHAPES,
  SHAPE_CATEGORIES,
  SHAPE_PROPS,
  SHAPE_TYPES
} from '@/constants/shapes';
import { roundCorners } from 'svg-round-corners';
import type { AttributeValue, Attribute } from '@/types/feed/attribute';
import {
  canonicalChoice,
  stringifyAttributeValue,
  defaultValueForTargetKey,
  getAttributeValueType,
  resolveLayerValue
} from '@/helpers/attributeValueTypes';
import { parseFontValue } from '@/helpers/fontValue';
import { languageCodeFor } from '@/constants/languageRegistry';
import { MediaType } from '@/types/layers/media';
import { requestCanvasRender } from '@/helpers/canvasRender';
import { clearAdRestoreCache } from '@/services/editor/adRestoreCache';
import { masonryLayout, GAP } from '@/helpers/masonryLayout';
import {
  composeWorldFromLocal,
  getAbsoluteTransform,
  pickTransform
} from '@/helpers/fabricTransforms';
import type { LayerTransformSnapshot } from '@/types/canvas/transform';
import type { SelectedItemsMap } from '@/types/canvas/selection';
import type {
  LayerId,
  AdId,
  Layer,
  StoredLayer,
  LayerMap,
  LayerOverrides,
  LayerPropertyKey,
  LayerType,
  TextLayer,
  ImageLayer,
  VideoLayer,
  AudioLayer
} from '@/types/layers/layer';
import type { OrbitState } from '@/types/library/threeD';
import type { UsedMediaRef } from '@/types/library/gallery';
import type {
  DimensionFacets,
  VariantOverrideMap
} from '@/types/feed/variantOverrides';
import { resolveOverrideEntry } from '@/helpers/variantOverrides';
import { useAttributeStore } from './attribute';
import { useUiStore } from './uiStore';
import type {
  AdConfig,
  EditorState,
  OverrideContext,
  RepositioningBackground
} from '@/types/stores/editor';

export const useEditorStore = defineStore('editor', {
  state: (): EditorState => ({
    ads: {},
    activeAdId: null,
    selectedLayerIds: [],
    selectedAdIds: [],
    layerizeModeActive: false,
    strictFocusAdId: null,
    layers: {},
    layerOrder: {},
    timelineOrder: [],
    adsPerRow: 3,
    viewports: {
      mosaic: { zoom: 0.5, pan: { x: 0, y: 0 } },
      variant: {
        zoom: 1,
        pan: { x: 0, y: 0 },
        selectedAdId: null,
        fittedAdId: null
      }
    },
    featureFlags: {},
    enteredElementPath: [],
    repositioningBackground: null,
    lastUsedFillColor: DEFAULT_SHAPE_COLOR,
    isTrimmingVideo: false,
    isTrimmingAudio: false,
    loopCount: 1,
    hiddenLayerPeek: {},
    structureRevision: 0
  }),

  // ── Getters ────────────────────────────────────────────────

  getters: {
    adsList: state => Object.values(state.ads),
    activeAd: state => (state.activeAdId ? state.ads[state.activeAdId] : null),

    /**
     * Resolve layer props: merge global + ad-specific overrides
     */
    getResolvedProps:
      state =>
      (layerId: LayerId, adId?: AdId): Layer | null => {
        const layer = state.layers[layerId];
        if (!layer) return null;

        const targetAdId = adId ?? state.activeAdId;
        const adOverrides = targetAdId
          ? layer.overrides[targetAdId]
          : undefined;

        const { overrides, ...globalProps } = layer;
        const resolved = adOverrides
          ? { ...globalProps, ...adOverrides }
          : globalProps;

        const attributeStore = useAttributeStore();
        const variantIndex = attributeStore.currentVariantIndex;
        injectMappedAttributes(
          resolved,
          layer,
          attr => attributeStore.variants[variantIndex]?.[attr.columnIndex],
          {
            overrides: attributeStore.variantOverrides,
            facets: buildActiveFacets(state, targetAdId, layer, variantIndex),
            variantId: attributeStore.variantIds[variantIndex]
          }
        );

        return resolved as Layer;
      },

    /**
     * The values `getResolvedProps` gives, worked out one prop at a time as
     * they are read. Every edit has the frame renderer and the animation end
     * read a few props of every layer, and copying all of each layer's props
     * for that was a large part of the edit. Only for reading props: the view
     * cannot be spread, listed or written.
     */
    getResolvedView(): (layerId: LayerId, adId?: AdId) => Layer | null {
      return (layerId, adId) => {
        const layer = this.layers[layerId];
        if (!layer) return null;
        const targetAdId = adId ?? this.activeAdId;
        const own = targetAdId ? layer.overrides[targetAdId] : undefined;
        return new Proxy({} as Layer, {
          get: (_, key) => {
            // a resolved layer has no overrides, and is never a Vue proxy
            if (typeof key !== 'string' || key === 'overrides') return;
            if (key.startsWith('__v')) return;
            if (layer.mappedAttributes?.[key]) {
              return this.getResolvedProps(layerId, adId)?.[key as keyof Layer];
            }
            return own && key in own
              ? own[key as keyof typeof own]
              : layer[key as keyof typeof layer];
          }
        });
      };
    },

    /**
     * World transform for a layer. Prefers the live Fabric object's
     * `calcTransformMatrix()` (single decompose covers the full ancestor
     * chain). Falls back to a store-only recursive compose when no Fabric
     * object is mounted yet (e.g. during action replay before projection).
     */
    getWorldTransform(): (
      layerId: LayerId,
      adId?: AdId
    ) => LayerTransformSnapshot | null {
      const fromFabric = (
        layerId: LayerId,
        adId?: AdId
      ): LayerTransformSnapshot | null => {
        const ad = adId ? this.ads[adId] : this.activeAd;
        const obj = ad?.objectMap.get(layerId);
        return obj ? getAbsoluteTransform(obj) : null;
      };

      const fromStore = (
        layerId: LayerId,
        adId?: AdId
      ): LayerTransformSnapshot | null => {
        const resolved = this.getResolvedProps(layerId, adId);
        if (!resolved) return null;

        const local = pickTransform(resolved);

        const layer = this.layers[layerId];
        if (!layer?.parentId) return local;

        const parentWorld = compute(layer.parentId, adId);
        return parentWorld ? composeWorldFromLocal(parentWorld, local) : local;
      };

      const compute = (
        layerId: LayerId,
        adId?: AdId
      ): LayerTransformSnapshot | null =>
        fromFabric(layerId, adId) ?? fromStore(layerId, adId);

      return compute;
    },

    /**
     * Read one layer's `visible` off the override chain instead of building a
     * whole resolved layer. Callers that only need visibility run this per
     * layer per ad on hot paths — drag selection, the per-card hidden-layer
     * badge — where a full resolve allocates a merged object and makes the
     * caller depend on every prop of every layer. Attribute mapping can
     * rewrite any prop, so mapped layers still take the full resolve.
     */
    resolveLayerVisible(): (
      layerId: LayerId,
      adId: AdId
    ) => boolean | undefined {
      return (layerId, adId) => {
        const layer = this.layers[layerId];
        if (!layer) return undefined;
        if (layer.mappedAttributes && 'visible' in layer.mappedAttributes) {
          return this.getResolvedProps(layerId, adId)?.visible;
        }
        return layer.overrides[adId]?.visible ?? layer.visible;
      };
    },

    /**
     * Resolve layer props for an explicit variant index — never reads or
     * mutates attributeStore.currentVariantIndex. Used by variant cells to
     * render arbitrary variants without disturbing the live editor.
     */
    getResolvedPropsForVariant:
      state =>
      (layerId: LayerId, adId: AdId, variantIndex: number): Layer | null => {
        const layer = state.layers[layerId];
        if (!layer) return null;

        const adOverrides = layer.overrides[adId];
        const { overrides, ...globalProps } = layer;
        const resolved = adOverrides
          ? { ...globalProps, ...adOverrides }
          : globalProps;

        const attributeStore = useAttributeStore();
        injectMappedAttributes(
          resolved,
          layer,
          attr => attributeStore.variants[variantIndex]?.[attr.columnIndex],
          {
            overrides: attributeStore.variantOverrides,
            facets: buildActiveFacets(state, adId, layer, variantIndex),
            variantId: attributeStore.variantIds[variantIndex]
          }
        );

        return resolved as Layer;
      },

    /**
     * Global layer fields (no ad-override merge) with mappedAttributes
     * resolved against an explicit variant index.
     */
    getGlobalResolvedProps:
      state =>
      (layerId: LayerId, variantIndex: number): Layer | null => {
        const layer = state.layers[layerId];
        if (!layer) return null;

        const { overrides, ...globalProps } = layer;

        const attributeStore = useAttributeStore();
        const facets = buildActiveFacets(state, null, layer, variantIndex);
        delete facets.ad;

        injectMappedAttributes(
          globalProps,
          layer,
          attr => attributeStore.variants[variantIndex]?.[attr.columnIndex],
          {
            overrides: attributeStore.variantOverrides,
            facets,
            variantId: attributeStore.variantIds[variantIndex]
          }
        );

        return globalProps as Layer;
      },

    /**
     * Get editable value for UI (respects edit mode)
     */
    getEditableValue:
      state =>
      (layerId: LayerId, propName: LayerPropertyKey): unknown => {
        const layer = state.layers[layerId];
        if (!layer) return undefined;

        // Stringified because property panels bind to text inputs that can't display raw booleans/numbers.
        const attributeStore = useAttributeStore();
        const ids = layer.mappedAttributes ?? {};
        const attrId = ids[propName as string];
        if (attrId) {
          const attr = attributeStore.getAttributeById(attrId);
          if (attr) {
            // Resolve the same way the canvas does, so a toolbar never shows
            // the base cell while the canvas paints an override.
            const entry = resolveOverrideEntry(
              attributeStore.variantOverrides,
              attributeStore.variantIds[attributeStore.currentVariantIndex],
              attrId,
              buildActiveFacets(state, undefined, layer)
            );
            const cell =
              entry === undefined
                ? attributeStore.getAttributeValue(attr)
                : entry.value;
            // Invalid/empty font falls back to the layer's own font so the toolbar matches the canvas.
            if (propName === FONT_FAMILY_KEY) {
              const resolved = resolveLayerValue(attr.targetKey, cell, layer.type);
              if (!resolved) return (layer as any)[propName];
              return stringifyAttributeValue(resolved as AttributeValue);
            }
            // a number or choice cell can fall back or scale, so show what is drawn
            if (isTypedLayerValue(attr.targetKey)) {
              return editableTypedValue(
                attr.targetKey,
                cell,
                layer,
                ownEditableValue(state, layer, propName)
              );
            }
            return stringifyAttributeValue(cell);
          }
        }

        return ownEditableValue(state, layer, propName);
      },

    /**
     * Check if property has local override
     */
    hasOverride:
      state =>
      (layerId: LayerId, propName: LayerPropertyKey): boolean => {
        const layer = state.layers[layerId];
        const adId = state.activeAdId;
        if (!layer || !adId) return false;
        return propName in (layer.overrides[adId] ?? {});
      },

    /**
     * Context a variant override applies to right now. Exposed so writers
     * build keys from the same facets the resolver reads.
     */
    activeFacets:
      state =>
      (adId?: AdId | null, layerId?: LayerId): DimensionFacets =>
        buildActiveFacets(
          state,
          adId,
          layerId ? state.layers[layerId] : undefined
        ),

    /**
     * Layers for current ad in z-order
     */
    currentAdLayers(): Layer[] {
      if (!this.activeAdId) return [];
      const order = this.layerOrder[this.activeAdId] ?? [];
      return order
        .map(id => this.getResolvedProps(id))
        .filter((l): l is Layer => l !== null);
    },

    /**
     * Get selected layer (first one)
     */
    selectedLayer(): StoredLayer | null {
      const firstId = this.selectedLayerIds[0];
      if (!firstId) return null;
      return this.layers[firstId] ?? null;
    },

    /**
     * Returns the selected layer id only when exactly ONE layer is selected.
     * Used by toolbars and dimension readouts that don't make sense on a
     * multi-select.
     */
    singleSelectedLayerId: state =>
      state.selectedLayerIds.length === 1
        ? (state.selectedLayerIds[0] ?? null)
        : null,

    singleSelectedTextLayer(): StoredLayer | null {
      if (this.selectedLayerIds.length !== 1) return null;
      const layer = this.layers[this.selectedLayerIds[0]!] ?? null;
      return layer && layer.type === LAYER_TYPES.TEXT ? layer : null;
    },

    isActiveAdStrictFocus: state =>
      state.strictFocusAdId !== null &&
      state.strictFocusAdId === state.activeAdId,

    isFeatureEnabled:
      state =>
      (key: string): boolean =>
        state.featureFlags[key] ??
        ADVANCED_FEATURES.find(f => f.key === key)?.default ??
        false,

    selectedItems(state): SelectedItemsMap {
      const adIds = Object.keys(state.ads);
      const result: SelectedItemsMap = {
        Text: new Set(),
        Image: new Set(),
        Shape: new Set(),
        Video: new Set(),
        Audio: new Set(),
        Element: new Set(),
        BackgroundGroup: new Set(),
        Attributes: new Set()
      };

      for (const layer of Object.values(state.layers)) {
        // Collect all mapped attribute IDs
        for (const id of Object.values(layer.mappedAttributes ?? {})) {
          result.Attributes.add(id);
        }

        switch (layer.type) {
          case LAYER_TYPES.IMAGE: {
            const l = layer as StoredLayer & ImageLayer;
            // Skip image URL collection only when the url slot is attribute-mapped
            if (l.mappedAttributes?.url) break;
            let allOverridden = true;
            for (const adId of adIds) {
              const ovUrl = (l.overrides[adId] as any)?.url;
              if (ovUrl) result.Image.add(ovUrl);
              else allOverridden = false;
            }
            if (!allOverridden && (l as any).url)
              result.Image.add((l as any).url);
            break;
          }

          case LAYER_TYPES.VIDEO: {
            const l = layer as StoredLayer & VideoLayer;
            // Skip video ID collection only when the url slot is attribute-mapped
            if (l.mappedAttributes?.url || l.mappedAttributes?.videoUrl) break;
            let allOverridden = true;
            for (const adId of adIds) {
              const ovId = (l.overrides[adId] as any)?.videoId;
              if (ovId) result.Video.add(ovId);
              else allOverridden = false;
            }
            if (!allOverridden && l.videoId) result.Video.add(l.videoId);
            break;
          }

          case LAYER_TYPES.AUDIO: {
            const l = layer as StoredLayer & AudioLayer;
            // Skip audio ID collection only when the url slot is attribute-mapped
            if (l.mappedAttributes?.url || l.mappedAttributes?.audioUrl) break;
            let allOverridden = true;
            for (const adId of adIds) {
              const ovId = (l.overrides[adId] as any)?.audioId;
              if (ovId) result.Audio.add(ovId);
              else allOverridden = false;
            }
            if (!allOverridden && l.audioId) result.Audio.add(l.audioId);
            break;
          }
        }
      }

      return result;
    },

    /**
     * Get all media (images, frames & videos) used in the template with their metadata.
     * 3D layers are excluded — their imageId points at a 3D asset, not a
     * media-library image, so reconciling it against image/frame endpoints
     * would surface wrong or phantom gallery entries.
     */
    usedMedia(state): UsedMediaRef[] {
      const result: UsedMediaRef[] = [];
      const seen = new Set<string>();

      for (const layer of Object.values(state.layers)) {
        if (
          layer.type !== LAYER_TYPES.IMAGE &&
          layer.type !== LAYER_TYPES.VIDEO &&
          layer.type !== LAYER_TYPES.AUDIO
        )
          continue;
        const l = layer as any;
        if (l.imageType === MediaType.ThreeD) continue;

        const collect = (id: string, url: string, type?: MediaType) => {
          if (!id || seen.has(id)) return;
          result.push({ id, url, mediaType: type });
          seen.add(id);
        };

        switch (layer.type) {
          case LAYER_TYPES.IMAGE: {
            collect(l.imageId, l.url, l.imageType);

            if (l.overrides) {
              for (const override of Object.values(l.overrides)) {
                const o = override as any;
                collect(o.imageId, o.url, o.imageType);
              }
            }

            break;
          }
          case LAYER_TYPES.VIDEO: {
            collect(l.videoId, l.url, MediaType.Video);

            if (l.overrides) {
              for (const override of Object.values(l.overrides)) {
                const o = override as any;
                collect(o.videoId, o.url, MediaType.Video);
              }
            }

            break;
          }
          case LAYER_TYPES.AUDIO: {
            collect(l.audioId, l.url, MediaType.Audio);

            if (l.overrides) {
              for (const override of Object.values(l.overrides)) {
                const o = override as any;
                collect(o.audioId, o.url, MediaType.Audio);
              }
            }

            break;
          }
          // No default
        }
      }
      return result;
    }
  },

  // ── Actions ────────────────────────────────────────────────

  actions: {
    getVisuallySortedAds(): AdConfig[] {
      const ads = this.adsList;
      if (ads.length <= 1) return ads;
      const numCols = Math.min(this.adsPerRow, ads.length);
      const layout = masonryLayout(ads, numCols, GAP);

      const getX = (adId: string) =>
        Math.round(layout.positions.get(adId)?.x ?? 0);
      const getY = (adId: string) =>
        Math.round(layout.positions.get(adId)?.y ?? 0);

      const uniqueX = [...new Set(ads.map(ad => getX(ad.id)))].toSorted(
        (a, b) => a - b
      );

      const columns = uniqueX.map(x => {
        return ads
          .filter(ad => getX(ad.id) === x)
          .toSorted((a, b) => getY(a.id) - getY(b.id));
      });

      const sortedAds: AdConfig[] = [];
      const maxRows = Math.max(...columns.map(col => col.length));
      for (let r = 0; r < maxRows; r++) {
        for (const col of columns) {
          if (col) {
            const ad = col[r];
            if (ad) {
              sortedAds.push(ad);
            }
          }
        }
      }

      return sortedAds;
    },

    // ── Ad Management ────────────────────────────────────────

    createAd(
      id: AdId,
      name: string,
      width: number,
      height: number,
      loopCount?: number
    ) {
      this.ads[id] = {
        id,
        name,
        width,
        height,
        canvas: null,
        objectMap: markRaw(new Map()),
        loopCount
      };
      const existingOrder = Object.values(this.layerOrder)[0] as
        | LayerId[]
        | undefined;
      this.layerOrder[id] = existingOrder
        ? [...existingOrder]
        : Object.keys(this.layers);
    },

    removeAd(adId: AdId) {
      const ad = this.ads[adId];
      if (ad?.canvas) {
        // Remove all event listeners and references before disposal
        ad.canvas.off();
        ad.canvas.clear();
        ad.objectMap.clear();
        ad.canvas.dispose();
      }
      delete this.ads[adId];
      delete this.layerOrder[adId];
      delete this.hiddenLayerPeek[adId];

      /**
       * Drop this ad's per-layer overrides too — left behind they are dead
       * weight that no ad can ever read again (a re-added size gets a fresh
       * id), and they would ride along into every save. Callers that need to
       * restore the ad snapshot the overrides before removing it.
       */
      for (const layer of Object.values(this.layers)) {
        delete layer.overrides[adId];
      }

      this.enteredElementPath = [];

      if (this.strictFocusAdId === adId) {
        this.strictFocusAdId = null;
      }

      if (this.activeAdId === adId) {
        const remaining = Object.keys(this.ads);
        this.activeAdId = remaining[0] ?? null;
      }
      this.pruneSelection();

      if (this.viewports.variant.selectedAdId === adId) {
        this.viewports.variant.selectedAdId = null;
      }
      if (this.viewports.variant.fittedAdId === adId) {
        this.viewports.variant.fittedAdId = null;
      }
    },

    /**
     * Mark that the layer structure changed. Called from the action
     * dispatcher for the action types that restructure layers — see
     * `structureRevision`.
     */
    bumpStructureRevision() {
      this.structureRevision++;
    },

    registerCanvas(adId: AdId, canvas: Canvas) {
      const ad = this.ads[adId];
      if (ad) {
        ad.canvas = markRaw(canvas);
      }
    },

    unregisterCanvas(adId: AdId) {
      const ad = this.ads[adId];
      if (ad?.canvas) {
        ad.canvas.dispose();
        ad.canvas = null;
        ad.objectMap.clear();
      }
    },

    /**
     * The one way to move focus between ads. Passing `null` drops focus
     * entirely (the "all sizes" mode). Leaving the previous ad tears down
     * its canvas selection chrome and the group the user had entered.
     */
    setActiveAd(adId: AdId | null) {
      if (this.activeAdId === adId) return;

      const prevAd = this.activeAd;
      if (prevAd?.canvas) {
        prevAd.canvas.discardActiveObject();
        requestCanvasRender(prevAd.canvas);
      }

      this.activeAdId = adId;
      this.enteredElementPath = [];

      // The selection follows the focus: one ad, or nothing at all when
      // focus is dropped.
      if (adId) this.setSelectionAds([adId]);
      else this.setSelectedLayers([]);
    },

    setSelectedLayers(layerIds: LayerId[]) {
      this.selectedLayerIds = [...new Set(layerIds)];
      this.isTrimmingVideo = false;
      this.isTrimmingAudio = false;

      // A real selection takes over from a highlight (see highlightLayers).
      if (this.selectedLayerIds.length > 0) useUiStore().setHighlight(null);

      // Layers and the ads they span live and die together.
      if (this.selectedLayerIds.length === 0) {
        this.selectedAdIds = [];
      } else if (this.selectedAdIds.length === 0 && this.activeAdId) {
        this.selectedAdIds = [this.activeAdId];
      }
    },

    /**
     * Set the ads the current selection spans. Ads that no longer exist
     * are dropped and the focused ad is always part of the set.
     */
    setSelectionAds(adIds: AdId[]) {
      if (!this.activeAdId || this.selectedLayerIds.length === 0) {
        this.selectedAdIds = [];
        return;
      }
      const next = new Set(adIds.filter(id => this.ads[id]));
      next.add(this.activeAdId);
      this.selectedAdIds = [...next];
    },

    /**
     * Drop selected layers and ads that no longer exist. Returns the
     * dropped layer ids so the caller can decide whether to re-sync.
     */
    pruneSelection(): LayerId[] {
      const removed = this.selectedLayerIds.filter(id => !this.layers[id]);
      if (removed.length > 0) {
        this.setSelectedLayers(
          this.selectedLayerIds.filter(id => this.layers[id])
        );
      }
      this.setSelectionAds(this.selectedAdIds);
      return removed;
    },

    setLayerizeModeActive(active: boolean) {
      this.layerizeModeActive = active;
    },


    enterElement(elementId: LayerId) {
      if (this.enteredElementPath.at(-1) === elementId) {
        return;
      }
      this.enteredElementPath.push(elementId);
    },

    exitElement(): LayerId | null {
      return this.enteredElementPath.pop() ?? null;
    },

    setRepositioningBackground(value: RepositioningBackground | null) {
      this.repositioningBackground = value;
    },

    clearEnteredPath() {
      this.enteredElementPath = [];
    },

    toggleStrictFocus(adId: AdId) {
      if (this.strictFocusAdId === adId) {
        this.strictFocusAdId = null;
      } else {
        this.strictFocusAdId = adId;
        this.setActiveAd(adId);
      }
    },

    toggleFeature(key: string) {
      const current = this.isFeatureEnabled(key);
      this.featureFlags[key] = !current;

      if (current) {
        const def = ADVANCED_FEATURES.find(f => f.key === key);
        def?.onDisable?.(this as any);
      }
    },

    /**
     * Replace the whole feature-flag map, running the same `onDisable` cleanup
     * `toggleFeature` does for any flag that flips on→off. Used when a sibling
     * tab's flag change arrives over cross-tab sync.
     */
    setFeatureFlags(flags: Record<string, boolean>) {
      for (const key of Object.keys(flags)) {
        const was = this.isFeatureEnabled(key);
        const now = flags[key] ?? false;
        this.featureFlags[key] = now;
        if (was && !now) {
          const def = ADVANCED_FEATURES.find(f => f.key === key);
          def?.onDisable?.(this as any);
        }
      }
    },

    resetEditor() {
      for (const adId of Object.keys(this.ads)) {
        this.unregisterCanvas(adId);
      }
      this.ads = {};
      this.layers = {};
      this.layerOrder = {};
      this.timelineOrder = [];
      this.selectedLayerIds = [];
      this.selectedAdIds = [];
      this.layerizeModeActive = false;
      this.activeAdId = null;
      this.strictFocusAdId = null;
      this.featureFlags = {};
      this.enteredElementPath = [];
      this.lastUsedFillColor = DEFAULT_SHAPE_COLOR;
      /**
       * Ad-keyed transient state. Cleared here because the incoming document's
       * ads can reuse ids the outgoing one used (cross-tab rehydrate, Element
       * Edit), and a stale entry would silently re-attach to an unrelated ad.
       */
      this.hiddenLayerPeek = {};
      this.viewports.variant.selectedAdId = null;
      this.viewports.variant.fittedAdId = null;

      /**
       * Deleted-ad layouts describe the template being torn down — they must
       * not leak into whatever loads next, where the same size could restore
       * a stranger's layout.
       */
      clearAdRestoreCache();
      this.structureRevision = 0;
    },

    loadFromDeserialized(data: {
      ads: Record<AdId, AdConfig>;
      layers: LayerMap;
      layerOrder: Record<AdId, LayerId[]>;
      timelineOrder?: LayerId[];
      adsPerRow: number;
      featureFlags: Record<string, boolean>;
      loopCount?: number;
    }) {
      this.ads = data.ads;
      this.layers = data.layers;
      this.layerOrder = data.layerOrder;
      this.timelineOrder = data.timelineOrder ?? [];
      this.adsPerRow = data.adsPerRow;
      this.featureFlags = data.featureFlags;
      this.loopCount = data.loopCount ?? 1;

      // Viewports are session-only — fit-to-screen on mount sets them.
      this.viewports = {
        mosaic: { zoom: 0.5, pan: { x: 0, y: 0 } },
        variant: {
          zoom: 1,
          pan: { x: 0, y: 0 },
          selectedAdId: null,
          fittedAdId: null
        }
      };

      this.activeAdId = null;
      this.strictFocusAdId = null;
      this.enteredElementPath = [];
      // Selection is never serialized, so the incoming template has none.
      this.selectedLayerIds = [];
      this.selectedAdIds = [];
      this.lastUsedFillColor = DEFAULT_SHAPE_COLOR;
    },

    updateLastUsedFillColor(color: string) {
      this.lastUsedFillColor = color;
    }
  }
});

/**
 * Helpers
 */

/**
 * The dimensions an override can be scoped to right now. Single place on
 * purpose — adding a dimension later (season) is an edit here and nowhere else.
 *
 * `ad` comes from editor state, but `lang` is a feed column: it varies per row
 * *and* per layer, since each layer binds its own Language attribute. Callers
 * that resolve for a specific layer/row must pass both or the facet is missed.
 */
function buildActiveFacets(
  state: EditorState,
  adId?: AdId | null,
  layer?: StoredLayer,
  variantIndex?: number
): DimensionFacets {
  const targetAdId = adId ?? state.activeAdId;
  const facets: DimensionFacets = targetAdId ? { ad: targetAdId } : {};

  const languageAttrId = layer?.mappedAttributes?.[LANGUAGE_KEY];
  if (!languageAttrId) return facets;

  const attributeStore = useAttributeStore();
  const attr = attributeStore.getAttributeById(languageAttrId);
  if (!attr) return facets;

  const row =
    attributeStore.variants[variantIndex ?? attributeStore.currentVariantIndex];
  const code = languageCodeFor(
    stringifyAttributeValue(row?.[attr.columnIndex] ?? '')
  );
  if (code) facets.lang = code;

  return facets;
}

function injectMappedAttributes(
  resolved: Record<string, unknown>,
  layer: StoredLayer,
  readCell: (attr: Attribute) => AttributeValue | undefined,
  ctx: OverrideContext
): void {
  const attributeStore = useAttributeStore();
  // the swapped shape goes first so a linked radius rounds the row's shape
  const ids = Object.entries(layer.mappedAttributes ?? {}).toSorted(
    ([a], [b]) =>
      Number(b === SHAPE_PROPS.PATH) - Number(a === SHAPE_PROPS.PATH)
  );
  for (const [propKey, attrId] of ids) {
    const attr = attributeStore.getAttributeById(attrId);
    if (!attr) continue;
    // A language is context, not paint — writing it would leave a phantom
    // property on the layer record and in history.
    if (propKey === LANGUAGE_KEY) continue;
    // An override shadows the cell for this context only — the feed row is
    // untouched. Existence decides, so an override holding '' still wins.
    const entry = resolveOverrideEntry(
      ctx.overrides,
      ctx.variantId,
      attrId,
      ctx.facets
    );
    const cell =
      entry === undefined
        ? (readCell(attr) ?? defaultValueForTargetKey(attr.targetKey))
        : entry.value;
    if (attr.targetKey === FONT_SCALE_KEY) {
      const percent = resolveLayerValue(attr.targetKey, cell, layer.type, 100);
      scaleFontSize(resolved, Number(percent) / 100);
      continue;
    }
    if (attr.targetKey === RADIUS_SCALE_KEY) {
      const percent = resolveLayerValue(attr.targetKey, cell, layer.type, 100);
      scaleBorderRadius(resolved, layer.type, Number(percent) / 100);
      continue;
    }
    if (attr.targetKey === SHAPE_KEY) {
      applyShapePreset(resolved, canonicalChoice(attr.targetKey, cell));
      continue;
    }
    if (propKey === FONT_FAMILY_KEY) {
      const resolvedFont = resolveLayerValue(attr.targetKey, cell, layer.type, resolved[propKey]);
      if (!resolvedFont) continue;
      resolved[propKey] = resolvedFont;
      const { weight, style } = parseFontValue(stringifyAttributeValue(cell));
      resolved['fontWeight'] = weight;
      resolved['fontStyle'] = style;
      continue;
    }
    resolved[propKey] = resolveLayerValue(
      attr.targetKey,
      cell,
      layer.type,
      resolved[propKey]
    );
  }
  if (layer.mappedAttributes?.[SHAPE_PROPS.PATH]) {
    // after every binding, so a linked stroke width of 0 cannot hide a swapped-in line
    if (isLinePath(resolved)) keepLineVisible(resolved);
    // a layer that never had a dash leaves it unset, which would keep the last line's dash on screen
    resolved[SHAPE_PROPS.STROKE_DASH_ARRAY] ??= '';
  }
}

function ownEditableValue(
  state: EditorState,
  layer: StoredLayer,
  propName: LayerPropertyKey
): unknown {
  const adId = state.activeAdId;
  if (!adId) return (layer as any)[propName];
  const override = layer.overrides[adId];
  return (override as any)?.[propName] ?? (layer as any)[propName];
}

function isTypedLayerValue(targetKey: string): boolean {
  const type = getAttributeValueType(targetKey);
  return (
    type === ATTRIBUTE_VALUE_TYPES.NUMBER ||
    type === ATTRIBUTE_VALUE_TYPES.CHOICE
  );
}

function editableTypedValue(
  targetKey: string,
  cell: AttributeValue,
  layer: StoredLayer,
  ownValue: unknown
): unknown {
  // the toolbar rounds from this path, so it must stay a path, not the name
  if (targetKey === SHAPE_KEY) {
    const name = canonicalChoice(targetKey, cell);
    return DEFAULT_SHAPES.find(s => s.name === name)?.path ?? ownValue;
  }
  const isScale = targetKey === FONT_SCALE_KEY || targetKey === RADIUS_SCALE_KEY;
  if (!isScale) return resolveLayerValue(targetKey, cell, layer.type, ownValue);
  if (
    targetKey === FONT_SCALE_KEY &&
    (layer as TextLayer).textMode === TEXT_MODE.AUTO_FIT
  ) {
    return ownValue;
  }
  const percent = resolveLayerValue(targetKey, cell, layer.type, 100);
  return ((Number(ownValue) || 0) * Number(percent)) / 100;
}

/**
 * Auto-fit owns `fontSize` and writes its fitted size back, so scaling that
 * would compound on every redraw; there the percent scales the cap instead.
 */
function scaleFontSize(resolved: Record<string, unknown>, scale: number): void {
  const cap = Number(resolved[TEXT_PROPS.AUTO_FIT_MAX_FONT_SIZE]) || 0;
  if (resolved[TEXT_PROPS.TEXT_MODE] === TEXT_MODE.AUTO_FIT) {
    if (cap > 0) resolved[TEXT_PROPS.AUTO_FIT_MAX_FONT_SIZE] = cap * scale;
    return;
  }
  resolved[TEXT_PROPS.FONT_SIZE] = Number(resolved[TEXT_PROPS.FONT_SIZE]) * scale;
}

const roundedPathCache = new Map<string, string>();

/** A shape draws its corners from `path`, so a new radius means a new path. */
function scaleBorderRadius(
  resolved: Record<string, unknown>,
  layerType: LayerType,
  scale: number
): void {
  // a line has no corners, so a row that swapped one in ignores the radius
  if (resolved[SHAPE_PROPS.IS_ZERO_HEIGHT] || isLinePath(resolved)) return;
  const radius = (Number(resolved[SHAPE_PROPS.BORDER_RADIUS]) || 0) * scale;
  resolved[SHAPE_PROPS.BORDER_RADIUS] = radius;
  if (layerType !== LAYER_TYPES.SHAPE) return;

  const source = String(
    resolved[SHAPE_PROPS.ORIGINAL_PATH] || resolved[SHAPE_PROPS.PATH] || ''
  );
  if (source) resolved[SHAPE_PROPS.PATH] = roundedPath(source, radius);
}

function roundedPath(source: string, radius: number): string {
  if (radius <= 0) return source;
  const key = `${radius}|${source}`;
  let path = roundedPathCache.get(key);
  if (path === undefined) {
    path = roundCorners(source, radius, 2)?.path || source;
    if (roundedPathCache.size >= 500) roundedPathCache.clear();
    roundedPathCache.set(key, path);
  }
  return path;
}

const LINE_PATHS = new Set(
  DEFAULT_SHAPES.filter(s => s.category === SHAPE_CATEGORIES.LINES).map(
    s => s.path
  )
);

function keepLineVisible(resolved: Record<string, unknown>): void {
  if (!(Number(resolved[SHAPE_PROPS.STROKE_WIDTH]) > 0)) {
    resolved[SHAPE_PROPS.STROKE_WIDTH] = 2;
  }
}

function isLinePath(resolved: Record<string, unknown>): boolean {
  return LINE_PATHS.has(String(resolved[SHAPE_PROPS.ORIGINAL_PATH] ?? ''));
}

/**
 * Swap in the row's preset the way the Shapes panel does: the layer keeps its
 * width on each size and takes the preset's own aspect, a line takes its own
 * dash and is never drawn invisible, and only closed shapes are rounded.
 */
function applyShapePreset(
  resolved: Record<string, unknown>,
  name: AttributeValue | undefined
): void {
  const preset = DEFAULT_SHAPES.find(s => s.name === name);
  if (!preset) return;
  const ownSource =
    resolved[SHAPE_PROPS.ORIGINAL_PATH] || resolved[SHAPE_PROPS.PATH];
  const isLine = preset.category === SHAPE_CATEGORIES.LINES;

  resolved.shapeType = SHAPE_TYPES.PATH;
  resolved.shapeSource = '';
  resolved[SHAPE_PROPS.ORIGINAL_PATH] = preset.path;
  resolved[SHAPE_PROPS.IS_ZERO_HEIGHT] = !!preset.isZeroHeight;
  if (preset.path !== ownSource) {
    resolved[LAYER_PROPS.SCALE_Y] = resolved[LAYER_PROPS.SCALE_X];
  }
  if (isLine) {
    resolved[SHAPE_PROPS.PATH] = preset.path;
    resolved[SHAPE_PROPS.STROKE_DASH_ARRAY] = preset.strokeDashArray ?? '';
    keepLineVisible(resolved);
    return;
  }
  resolved[SHAPE_PROPS.PATH] = roundedPath(
    preset.path,
    Number(resolved[SHAPE_PROPS.BORDER_RADIUS]) || 0
  );
}
